import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';


import { handleSorting } from 'store/Admin/OrderList';
import { useDeadlineFilter } from 'utils/hooks/useDeadlineFilter';
import { usePagination } from 'utils/hooks/usePagination';
import { useHistoryState } from 'utils/hooks/useHistoryState';
import { formatDate, formatDateShort } from 'utils/DateUtils';
import {
  ORDERSTATUS,
  FILTER_ORDER_TYPE_LIST,
  ORDER_TYPE,
  pageNbOptions,
} from 'utils/Constants';
import { filterOrders } from 'helpers/FilterOrders';

import { Paginator } from 'components/shared/Paginator';
import { Loader } from 'components/shared/Loader';
import TablePagination from 'components/shared/Pagination/TablePagination';
import ErrorComponent from 'components/shared/ErrorComponent';
import { ProgressBar } from 'components/shared/ProgressBar';
import { CircularBar } from 'components/shared/CircularBar';
import { CustomDropdownReadOnly } from 'components/shared/DropDown/CustomDropdown';
import OrderTypeFilter from 'components/shared/Filter/OrderTypeFilter';
import DeadlineFilterPopover from 'components/shared/Filter/DeadlineFilterPopover';
import { Button } from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon } from 'icons/ChevronIcon';
import DataTable from 'components/shared/DataTable';
import { DownloadTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import AddIcon from '@mui/icons-material/Add';
import './styles.css';

const shouldIncludeType = (status) =>
    ![ORDERSTATUS.SMART, ORDERSTATUS.SMART_WITH_REVIEW].includes(status);

const getCommonColumns = ({
                            t,
                            selectedYear,
                            setSelectedYear,
                            startDate,
                            setStartDate,
                            endDate,
                            setEndDate,
                            selectedType,
                            setSelectedType,
                            handleTypeClick,
                            handleTypeClose,
                            anchorEl,
                            handleWritersClick,
                            handleWritersClose,
                          }) => [
  {
    header: t('Mot clé'),
    accessor: 'title',
    sortable: true,
    width: '50%',
  },
  {
    header: t('Agent IA'),
    accessor: 'agentName',
    sortable: false,
    width: '15%',
  },
  {
    header: t("Ajout"),
    accessor: 'dateAdded',
    sortable: true,
    width: '10%',
  },
  {
    header: t('Mise à jour'),
    accessor: 'dateUpdated',
    sortable: true,
    width: '15%',
  },

  {
    header: t('Status'),
    accessor: 'status',
    sortable: true,
    width: '20%',
  },
];

export default function SmarttextContentTable({
  orderStatus,
  texts,
  isLoading,
  isError,
  fetchDisplayData,
  onRowClickRedirectPath,
  onNewContentClick,
  onDelete,
  onLinkToProject,
  enableStatusDropdown = false,
  onStatusChange,
  allowedStatusKeys = null,
  disableEmptyStateCreateButton = false,
  hideEmptyStateCreateButton = false,
  readOnlyMode = false,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [textsPerPage, setTextsPerPage] = useState(10);
  const [selectedType, setSelectedType] = useHistoryState('selectedFilter', FILTER_ORDER_TYPE_LIST[0]);
  const [titleSearch] = useHistoryState('titleSearch', '');
  const [anchorEl, setAnchorEl] = useState(null);
  const [titleFilteredTexts, setTitleFilteredTexts] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [statusOverrides, setStatusOverrides] = useState({});
  const [openActionsFor, setOpenActionsFor] = useState(null);
  const [openStatusFor, setOpenStatusFor] = useState(null);
  const [statusMenuCoords, setStatusMenuCoords] = useState({ top: 0, left: 0 });
  const [actionsMenuCoords, setActionsMenuCoords] = useState({ top: 0, left: 0 });
  const tableContainerRef = useRef(null);

  const statusDefinitions = useMemo(
    () => ({
      DRAFT: {
        value: 'Brouillon',
        label: t('Brouillon'),
      },
      DELIVERED: {
        value: 'Livré',
        label: t('Livré'),
      },
      IN_REVISION: {
        value: 'En révision',
        label: t('En révision'),
      },
      VALIDATED: {
        value: 'Validé',
        label: t('Validé'),
      },
      ONLINE: {
        value: 'En ligne',
        label: t('En ligne'),
      },
    }),
    [t]
  );

  const selectableStatusEntries = useMemo(() => {
    const entries = Object.entries(statusDefinitions);
    if (!Array.isArray(allowedStatusKeys) || allowedStatusKeys.length === 0) {
      return entries;
    }
    const allowed = new Set(allowedStatusKeys);
    return entries.filter(([key]) => allowed.has(key));
  }, [statusDefinitions, allowedStatusKeys]);

  const getStatusIconByKey = (statusKey) => {
    switch (statusKey) {
      case 'ONLINE':
        return '/Images/deployed-text-icon.png';
      case 'VALIDATED':
        return '/Images/validate-text-icon.png';
      case 'DELIVERED':
        return '/Images/delivred-text-icon.png';
      case 'IN_REVISION':
        return '/Images/en_revision.png';
      case 'DRAFT':
      default:
        return '/Images/drafted-text-icon.png';
    }
  };

  useEffect(() => {
    setStatusOverrides({});
  }, [texts]);

  // Fermer menus statut / actions
  useEffect(() => {
    if (!openStatusFor && !openActionsFor) return;

    const handleClickOutside = (event) => {
      if (
        tableContainerRef.current &&
        !tableContainerRef.current.contains(event.target)
      ) {
        setOpenStatusFor(null);
        setOpenActionsFor(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenStatusFor(null);
        setOpenActionsFor(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openStatusFor, openActionsFor]);

  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
  }, []);

  // Nettoyage des textes
  const sanitizedTexts = useMemo(() => {
    return Array.isArray(texts) ? texts : [];
  }, [texts]);

  useEffect(() => {
    if (!sanitizedTexts || sanitizedTexts.length === 0) {
      setTitleFilteredTexts([]);
      return;
    }
    const filtered = filterOrders(sanitizedTexts, selectedType, titleSearch, true);
    if (titleSearch.length > 0) setCurrentPage(1);
    setTitleFilteredTexts(filtered);
  }, [sanitizedTexts, selectedType, titleSearch]);

  const {
    filteredOrders: filteredTexts,
    selectedYear,
    setSelectedYear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useDeadlineFilter(titleFilteredTexts);

  const sortedTexts = useMemo(() => {
    if (!Array.isArray(filteredTexts) || !sortField) return filteredTexts || [];
    const copy = [...filteredTexts];
    const isDateField = /date/i.test(sortField);
    copy.sort((a, b) => {
      const va = a?.[sortField];
      const vb = b?.[sortField];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let da = va;
      let db = vb;
      if (isDateField) {
        da = new Date(va).getTime();
        db = new Date(vb).getTime();
      }
      if (da < db) return sortOrder === 'asc' ? -1 : 1;
      if (da > db) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredTexts, sortField, sortOrder]);

  const { currentPage, setCurrentPage, totalPages, paginatedItems } =
      usePagination(sortedTexts, textsPerPage);

  // Lignes normales
  const baseRows = paginatedItems.map((text) => ({
    ...text,
    title: text.title,
    dateAdded: text.dateAdded ? formatDateShort(text.dateAdded) : text.dateAdded,
    dateUpdated: text.dateUpdated ? formatDateShort(text.dateUpdated) : text.dateUpdated,
    contentType: text.contentType || '-',
    score: text.score || '-',
    agentName: text.agentName || '-',
  }));

  const rows = baseRows;

  const handleTypeClick = (idx, e) => setAnchorEl({ [idx]: e.currentTarget });
  const handleTypeClose = () => setAnchorEl(null);
  const handleWritersClick = handleTypeClick;
  const handleWritersClose = handleTypeClose;

  const handleRowClick = (row) => {
    if (fetchDisplayData) {
      fetchDisplayData(row.id, row.type);
    } else {
      history.push(`${onRowClickRedirectPath}/${row.id}`);
    }
  };

  const getStatusValue = (row) => {
    if (!row) return 'DRAFT';
    if (statusOverrides[row.id]) return statusOverrides[row.id];
    if (row.deployed) return 'ONLINE';
    if (row.validated) return 'VALIDATED';
    if (row.inRevision) return 'IN_REVISION';
    if (row.delivered) return 'DELIVERED';
    return 'DRAFT';
  };

  const handleStatusSelect = (row, value) => {
    if (!row) return;
    const selectedEntry = selectableStatusEntries.find(([, def]) => def.value === value);
    if (!selectedEntry) {
      setOpenStatusFor(null);
      return;
    }
    if (typeof onStatusChange === 'function') {
      onStatusChange(row, value);
    } else {
      setStatusOverrides((prev) => ({ ...prev, [row.id]: value }));
    }
    setOpenStatusFor(null);
  };

  const handleStatusTriggerClick = (row, event) => {
    event.stopPropagation();
    if (openStatusFor === row.id) {
      setOpenStatusFor(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const estimatedHeight = selectableStatusEntries.length * 40 + 16;
    const menuWidth = 180;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    let top = rect.bottom + 4;
    if (top + estimatedHeight > viewportHeight) {
      top = rect.top - estimatedHeight - 4;
    }

    let left = rect.left;
    if (left + menuWidth > viewportWidth - 8) {
      left = viewportWidth - menuWidth - 8;
    }
    if (left < 8) left = 8;

    setStatusMenuCoords({ top, left });
    setOpenStatusFor(row.id);
  };

  const handleActionsClick = (e, rowId) => {
    e.stopPropagation();
    if (openActionsFor === rowId) {
      setOpenActionsFor(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const estimatedHeight = typeof onLinkToProject === 'function' ? 160 : 120;
    const menuWidth = 190;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    let top = rect.bottom + 4;
    if (top + estimatedHeight > viewportHeight) {
      top = rect.top - estimatedHeight - 4;
    }

    let left = rect.left;
    if (left + menuWidth > viewportWidth - 8) {
      left = viewportWidth - menuWidth - 8;
    }
    if (left < 8) left = 8;

    setActionsMenuCoords({ top, left });
    setOpenActionsFor(rowId);
  };

  const commonColumns = getCommonColumns({
    t,
    selectedYear,
    setSelectedYear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedType,
    setSelectedType,
    handleTypeClick,
    handleTypeClose,
    anchorEl,
    handleWritersClick,
    handleWritersClose,
  });

  const columns = [
    // 1) Mot clé
    {
      header: t('Mot clé'),
      accessor: 'title',
      sortable: true,
      width: readOnlyMode ? '55%' : '50%',
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#111827',
            }}
          >
            {row.title}
          </span>
          {row.url && (
            <span
              title={row.url}
              style={{
                fontSize: 12,
                color: '#1A1A1A',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                display: 'block',
              }}
            >
              {row.url}
            </span>
          )}
        </div>
      ),
    },
    // 2) Agent IA (nom + modèle)
    {
      header: t('Agent IA'),
      accessor: 'agentName',
      sortable: false,
      width: '15%',
      renderCell: (row) => {
        const name = row.agentName || (row.aiAgent && row.aiAgent.name) || '';
        const model = row.aiAgent && row.aiAgent.model ? row.aiAgent.model : null;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span
              style={{
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {name}
            </span>
            {model && (
              <span
                style={{
                  fontSize: 12,
                  color: '#6B7280',
                }}
              >
             LLM : {model}
              </span>
            )}
          </div>
        );
      },
    },
    // 3) Score
    {
      header: t('Score'),
      accessor: 'score',
      sortable: false,
      width: '4%',
      renderCell: (row) => (
        <span
          style={{
            display: 'block',
            textAlign: 'center',
            color: '#000000',
            fontWeight: 600,
          }}
        >
          {row.contentScore && typeof row.contentScore.globalScore !== 'undefined'
            ? row.contentScore.globalScore
            : ''}%
        </span>
      ),
    },
    // 4) Mise à jour
    {
      header: t('Mise à jour'),
      accessor: 'dateUpdated',
      sortable: true,
      width: '12%',
      renderCell: (row) => (row.dateUpdated || ''),
    },
    // 5) Ajout
    {
      header: t('Ajout'),
      accessor: 'dateAdded',
      sortable: true,
      width: '10%',
      renderCell: (row) => (row.dateAdded || ''),
    },
    // 6) Statut
    {
      header: t('Statut'),
      accessor: 'status',
      sortable: true,
      width: '25%',
      renderCell: (row) => {
        if (!enableStatusDropdown) {
          return row.status ? t(row.status) : '';
        }
        const currentValue = getStatusValue(row);
        const meta = statusDefinitions[currentValue] || statusDefinitions.DRAFT;
        const iconSrc = getStatusIconByKey(currentValue);
        return (
          <div
            style={{
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={iconSrc}
              alt={meta.label}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
              onClick={(e) => handleStatusTriggerClick(row, e)}
            />
            <span>{meta.label}</span>
            <img
              src="/Images/status-icon.png"
              alt="status icon"
              style={{ width: 16, height: 16, cursor: 'pointer' }}
              onClick={(e) => handleStatusTriggerClick(row, e)}
            />
            {openStatusFor === row.id && (
              <div
                style={{
                  position: 'fixed',
                  top: statusMenuCoords.top,
                  left: statusMenuCoords.left,
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(15,23,42,0.15)',
                  borderRadius: 8,
                  padding: '6px 0',
                  minWidth: 180,
                  zIndex: 1000,
                }}
              >
                {selectableStatusEntries.map(([key, def]) => (
                  <button
                    type="button"
                    key={key}
                    style={{
                      width: '100%',
                      padding: '6px 12px',
                      background: 'transparent',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#111827',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusSelect(row, def.value);
                    }}
                  >
                    <img
                      src={getStatusIconByKey(key)}
                      alt={def.label}
                      style={{ width: 14, height: 14 }}
                    />
                    <span>{def.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    ...(!readOnlyMode
      ? [
          {
      header: t('Action'),
      accessor: 'actions',
      sortable: false,
      width: '8%',
      renderCell: (row) => (
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            borderLeft: '1px solid #E5E7EB',
            paddingLeft: 8,
            position: 'relative',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src="/Images/edit-icon.png"
            alt="edit icon"
            style={{ width: 16, height: 16, cursor: 'pointer' }}
            onClick={() => handleRowClick(row)}
          />
          <img
            src="/Images/plus-icon.png"
            alt="plus icon"
            style={{ width: 16, height: 16, cursor: 'pointer' }}
            onClick={(e) => handleActionsClick(e, row.id)}
          />
          {openActionsFor === row.id && (
            <div
              style={{
                position: 'fixed',
                top: actionsMenuCoords.top,
                left: actionsMenuCoords.left,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
                borderRadius: 10,
                padding: '8px 0',
                minWidth: 190,
                zIndex: 1000,
              }}
            >
              <button
                type="button"
                style={{
                  width: '100%',
                  padding: '6px 14px',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  fontSize: 13,
                  color: '#111827',
                  cursor: 'pointer',
                  display: 'block',
                }}
                onClick={async () => {
                  try {
                    await DownloadTextWordType(row.id);
                  } catch (e) {
                    console.error('Erreur lors du téléchargement:', e);
                  } finally {
                    setOpenActionsFor(null);
                  }
                }}
              >
                {t('Telecharger le texte')}
              </button>

              {typeof onLinkToProject === 'function' && (
                <>
                  <div
                    style={{
                      borderTop: '1px solid #F3F4F6',
                      margin: '4px 0',
                    }}
                  />
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      padding: '6px 14px',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      fontSize: 13,
                      color: '#111827',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onLinkToProject(row);
                      setOpenActionsFor(null);
                    }}
                  >
                    <img
                      src="/Images/lier_projet_icon.png"
                      alt=""
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    {t('Lier à un projet')}
                  </button>
                </>
              )}

              {typeof onDelete === 'function' && (
                <>
                  <div
                    style={{
                      borderTop: '1px solid #F3F4F6',
                      margin: '4px 0',
                    }}
                  />
                  <button
                    type="button"
                    style={{
                      width: '90%',
                      padding: '6px 14px',
                      textAlign: 'left',
                      background: '#F1F1F1',
                      border: 'none',
                      fontSize: 13,
                      color: '#B91C1C',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      borderRadius: 6,
                      marginLeft: '5%',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(row);
                      setOpenActionsFor(null);
                    }}
                  >
                    <img
                      src="/Images/icon-trash.png"
                      alt="delete"
                      style={{ width: 25, height: 25 }}
                    />
                    <span>{t('Supprimer')}</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ),
    },
        ]
      : []),
  ];

  const handleSortingChange = (field) => {
    const direction = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    dispatch(
        handleSorting({
          table: orderStatus,
          sortField: field,
          sortOrder: direction,
        })
    );
    setSortField(field);
    setSortOrder(direction);
  };

  const emptyStateContent = (
      <div style={{
        textAlign: 'center',
        padding: '60px 40px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        margin: '20px 0',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ width: '100%', maxWidth: 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <img src="/Images/ai-img.png" alt="ai-img" style={{ width: '60px', height: '60px' }} />
          </div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '20px',
            fontFamily: 'Poppins, sans-serif !important'
          }}>
              {t('Créez, optimisez, performez : votre contenu prend une nouvelle dimension.')}
          </h2>
          <p style={{
            fontSize: '18px',
            color: '#666',
            lineHeight: '1.6',
            marginBottom: '30px',
            fontFamily: 'Poppins, sans-serif !important'
          }}>
              {t('La création de contenu n\'a jamais été aussi smart.')}
          </p>
          <p style={{
            fontSize: '16px',
            color: '#555',
            lineHeight: '1.7',
            marginBottom: readOnlyMode ? '0' : '40px',
            fontFamily: 'Poppins, sans-serif !important'
          }}>
              {readOnlyMode
                ? t('Aucun contenu IA disponible pour ce site.')
                : t('Boostez votre productivité grâce à la puissance multi-IA. Comparez et combinez les meilleures propositions issues de plusieurs LLM, puis optimisez-les grâce à un score de contenu en temps réel (SEO, lisibilité, pertinence)')}
          </p>
          {!hideEmptyStateCreateButton && (
          <button
              type="button"
              disabled={disableEmptyStateCreateButton}
              aria-disabled={disableEmptyStateCreateButton}
              style={{
                backgroundColor: '#6458F1',
                borderColor: '#6458F1',
                color: 'white',
                fontFamily: 'Poppins, sans-serif !important',
                padding: '12px 24px',
                borderRadius: '25px',
                border: 'none',
                cursor: disableEmptyStateCreateButton ? 'not-allowed' : 'pointer',
                opacity: disableEmptyStateCreateButton ? 0.55 : 1,
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 2px 8px rgba(100, 88, 241, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                if (disableEmptyStateCreateButton) return;
                e.target.style.backgroundColor = '#5548e0';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(100, 88, 241, 0.4)';
              }}
              onMouseOut={(e) => {
                if (disableEmptyStateCreateButton) return;
                e.target.style.backgroundColor = '#6458F1';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(100, 88, 241, 0.3)';
              }}
              onClick={() => {
                if (disableEmptyStateCreateButton) return;
                if (typeof onNewContentClick === 'function') {
                  onNewContentClick();
                } else {
                  history.push(onRowClickRedirectPath);
                }
              }}
          >
              {t('Je crée mon premier contenu SEO friendly')}
          </button>
          )}
        </div>
      </div>
  );

  useEffect(() => {
    if (!isLoading) setHasLoadedOnce(true);
  }, [isLoading]);

  return (
    <div className="content-center" ref={tableContainerRef}>
      {isError ? (
        <ErrorComponent />
      ) : (
        <>
          <DataTable
            headerClassName="ai-smart-table-head"
            columns={columns}
            data={rows}
            enableSorting
            onRowClick={handleRowClick}
            onSort={handleSortingChange}
            sortField={sortField}
            sortOrder={sortOrder}
            emptyStateContent={
              isLoading || !hasLoadedOnce ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '12px 8px',
                    backgroundColor: '#F6F6F6',
                    borderRadius: '6px',
                    margin: '6px 0',
                    width: '100%',
                    boxSizing: 'border-box',
                    color: '#666',
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                >
                  {t('Chargement...')}
                </div>
              ) : (
                emptyStateContent
              )
            }
          />

          {!isLoading && rows.length > 0 && (
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disablePrev={currentPage === 1}
              disableNext={currentPage === totalPages || rows.length === 0}
            />
          )}
        </>
      )}
    </div>
  );
}